'use client';

import { useEffect, useRef, useCallback } from 'react';
import gsap from 'gsap';
import { isTouchDevice } from './utils.js';

/**
 * Hook for magnetic button effect
 * @param {Object} options - Magnetic options
 * @returns {Object} ref
 */
export function useMagnetic(options = {}) {
  const {
    strength = 0.3,
    ease = 'power2.out',
    duration = 0.3,
    enabled = true,
  } = options;

  const ref = useRef(null);
  const bounds = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!ref.current || !bounds.current) return;

    const { clientX, clientY } = e;
    const { left, top, width, height } = bounds.current;

    const x = (clientX - left - width / 2) * strength;
    const y = (clientY - top - height / 2) * strength;

    gsap.to(ref.current, {
      x,
      y,
      duration,
      ease,
    });
  }, [strength, duration, ease]);

  const handleMouseLeave = useCallback(() => {
    if (!ref.current) return;

    gsap.to(ref.current, {
      x: 0,
      y: 0,
      duration,
      ease,
    });
  }, [duration, ease]);

  const handleMouseEnter = useCallback(() => {
    if (!ref.current) return;
    bounds.current = ref.current.getBoundingClientRect();
  }, []);

  useEffect(() => {
    if (!ref.current || !enabled || isTouchDevice()) return;

    const element = ref.current;

    element.addEventListener('mouseenter', handleMouseEnter);
    element.addEventListener('mousemove', handleMouseMove);
    element.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      element.removeEventListener('mouseenter', handleMouseEnter);
      element.removeEventListener('mousemove', handleMouseMove);
      element.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [enabled, handleMouseEnter, handleMouseMove, handleMouseLeave]);

  return ref;
}

export default useMagnetic;
